import React, { ReactNode, useState } from "react";
import { useTranslation } from "react-i18next";
import styles from "./NestedDataTable.module.scss";
import Edit from "../../../assets/images/actions/Edit";
import Delete from "../../../assets/images/actions/Delete";
import ArrowLeft from "../../../assets/images/actions/ArrowLeft";
import ArrowRight from "../../../assets/images/actions/ArrowRight";
import Up from "../../../assets/images/table/Up";
import Down from "../../../assets/images/table/Down";
import Loading from "../loading/Loading";
import Minus from "../../../assets/images/svgs/Minus";
import Plus from "../../../assets/images/svgs/Plus";
import AddIcon from "../../../assets/images/actions/AddIcon";

type RenderFn = (value: any, row?: any, index?: number) => React.ReactNode;

export interface Column {
  key: string;
  header?: string | ReactNode;
  render?: RenderFn;
  filter?: boolean;
}
export interface PaginationData {
  total: number | null;
  page?: number;
  limit?: number;
  onPageChange?: (page: number) => void;
  onLimitChange?: (limit: number) => void;
}

interface DataTableProps {
  data: any[];
  columns?: Column[];
  className?: string;
  noDataMessage?: string;
  actions?: boolean;
  onEdit?: (id: number) => void;
  onDelete?: (id: number) => void;
  onAddSubcategory?: (parentId: number, parentName: string) => void;
  onAddAttributes?: (categoryId: number, categoryName: string) => void;
  table_title?: string;
  paginationData?: PaginationData;
  other?: boolean;
  loading?: boolean;
}

interface ExpandedRows {
  [key: number]: boolean;
}

export default function NestedDataTable({
  data,
  columns,
  className = "",
  noDataMessage = "No data",
  actions,
  onEdit,
  onDelete,
  onAddSubcategory,
  onAddAttributes,
  table_title,
  paginationData,
  other = false,
  loading,
}: DataTableProps) {
  const { t } = useTranslation("translation");
  const [expandedRows, setExpandedRows] = useState<ExpandedRows>({});

  const inferredColumns: Column[] = React.useMemo(() => {
    if (columns && columns.length > 0) return columns;
    if (!data || data.length === 0) return [];

    const first = data[0];
    const keys = Object.keys(first);
    return keys.map((k) => ({ key: k, header: k }));
  }, [columns, data]);

  const cols = inferredColumns;

  const toggleRow = (rowId: number) => {
    setExpandedRows((prev) => ({
      ...prev,
      [rowId]: !prev[rowId],
    }));
  };

  const hasChildren = (row: any): boolean => {
    return (
      row.children && Array.isArray(row.children) && row.children.length > 0
    );
  };

  const handleAddSubcategory = (row: any, e: React.MouseEvent) => {
    e.stopPropagation();
    if (onAddSubcategory) {
      onAddSubcategory(row.id, row.name);
    }
  };

  const handleAddAttributes = (row: any, e: React.MouseEvent) => {
    e.stopPropagation();
    if (onAddAttributes) {
      onAddAttributes(row.id, row.name);
    }
  };

  const renderRow = (row: any, rowIndex: number, level: number = 0) => {
    const isExpanded = expandedRows[row.id];
    const children = row.children || [];
    const hasChildrenItems = hasChildren(row);

    return (
      <React.Fragment key={`${row.id}-${level}`}>
        <tr
          className={`${styles.tr} ${other ? styles.other_tr : ""} ${
            level > 0 ? styles.nested_row : ""
          }`}
          style={{
            backgroundColor:
              level > 0 ? `rgba(203, 60, 255, ${0.05 * level})` : undefined,
          }}
        >
          {cols.map((c, colIndex) => {
            const value = row == null ? undefined : row[c.key];

            if (colIndex === 0) {
              return (
                <td
                  key={c.key}
                  className={`${styles.td} ${other ? styles.other_td : ""}`}
                  style={{
                    paddingLeft: `${20 + level * 24}px`,
                    position: "relative",
                  }}
                >
                  <div className={styles.nested_cell}>
                    {hasChildrenItems && (
                      <button
                        className={styles.toggle_button}
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleRow(row.id);
                        }}
                      >
                        {isExpanded ? <Minus /> : <Plus />}
                      </button>
                    )}
                    {!hasChildrenItems && (
                      <span className={styles.no_children_indent}></span>
                    )}
                    <span className={styles.cell_content}>
                      {c.render
                        ? c.render(value, row, rowIndex)
                        : (value ?? "")}
                    </span>
                  </div>
                </td>
              );
            }

            return (
              <td
                key={c.key}
                className={`${styles.td} ${other ? styles.other_td : ""}`}
              >
                <p>
                  {c.render ? c.render(value, row, rowIndex) : (value ?? "")}
                </p>
              </td>
            );
          })}
          {actions && (
            <td className={`${styles.td} ${other ? styles.other_td : ""}`}>
              <div className={styles.icons}>
                {onAddAttributes && (
                  <div
                    className={`${styles.icon} ${styles.attributes_icon}`}
                    onClick={(e) => handleAddAttributes(row, e)}
                    title="Add Attributes"
                  >
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <rect
                        x="3"
                        y="3"
                        width="7"
                        height="7"
                        rx="1.5"
                        stroke="currentColor"
                        strokeWidth="2"
                      />
                      <rect
                        x="14"
                        y="3"
                        width="7"
                        height="7"
                        rx="1.5"
                        stroke="currentColor"
                        strokeWidth="2"
                      />
                      <rect
                        x="3"
                        y="14"
                        width="7"
                        height="7"
                        rx="1.5"
                        stroke="currentColor"
                        strokeWidth="2"
                      />
                      <path
                        d="M17.5 14V21M14 17.5H21"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                      />
                    </svg>
                  </div>
                )}
                {onAddSubcategory && (
                  <div
                    className={`${styles.icon} ${styles.add_icon}`}
                    onClick={(e) => handleAddSubcategory(row, e)}
                    title="Add Subcategory"
                  >
                    <AddIcon />
                  </div>
                )}
                {onEdit && (
                  <div
                    className={`${styles.icon} ${styles.edit_icon}`}
                    onClick={() => onEdit(row.id)}
                    title="Edit"
                  >
                    <Edit />
                  </div>
                )}
                {onDelete && (
                  <div
                    className={`${styles.icon} ${styles.delete_icon}`}
                    onClick={() => onDelete(row.id)}
                    title="Delete"
                  >
                    <Delete />
                  </div>
                )}
              </div>
            </td>
          )}
        </tr>

        {isExpanded &&
          hasChildrenItems &&
          children.map((child: any, childIndex: number) =>
            renderRow(child, childIndex, level + 1),
          )}
      </React.Fragment>
    );
  };

  if (loading) {
    return <Loading />;
  }

  if (!data || data.length === 0) {
    return (
      <div className={styles.no_data_wrapper}>
        <div className={styles.noData}>{noDataMessage}</div>
      </div>
    );
  }

  const totalPages = paginationData?.total
    ? Math.ceil(paginationData.total / (paginationData.limit || 10))
    : 1;

  const start =
    ((paginationData?.page || 1) - 1) * (paginationData?.limit || 10) + 1;
  const end = Math.min(
    start + (paginationData?.limit || 10) - 1,
    paginationData?.total || 0,
  );

  return (
    <div className={styles.data_table}>
      <div className={`${styles.wrapper} ${className}`}>
        <div className={styles.header}>
          <h1 className={styles.table_title}>{table_title}</h1>
          {paginationData && (
            <div>
              <div className={styles.pagination_pages}>
                <p className={styles.current_pages}>
                  {start}-{end}
                </p>
                <p className={styles.all_pages}>of {paginationData.total}</p>
              </div>
              <p className={styles.page_info}>
                Page {paginationData.page} of {totalPages}
              </p>
            </div>
          )}
        </div>
        <table className={`${styles.table} ${other ? styles.other_table : ""}`}>
          <thead>
            <tr>
              {cols.map((c) => (
                <th
                  key={c.key}
                  className={`${styles.th} ${other ? styles.other_th : ""}`}
                >
                  <div className={styles.header_cell}>
                    <p className={styles.key}> {c.header ?? c.key}</p>
                    {c.filter && (
                      <div className={styles.filters}>
                        <div className={styles.filter}>
                          <Up />
                        </div>
                        <div className={styles.filter}>
                          <Down />
                        </div>
                      </div>
                    )}
                  </div>
                </th>
              ))}
              {actions && (
                <th className={`${styles.th} ${other ? styles.other_th : ""} `}>
                  <div className={styles.header_cell}>
                    <p className={styles.key}>
                      {t("common.actions") as string}
                    </p>
                  </div>
                </th>
              )}
            </tr>
          </thead>
          <tbody>{data.map((row, rowIndex) => renderRow(row, rowIndex))}</tbody>
        </table>
      </div>
      {paginationData && (
        <div className={styles.pagination_controls}>
          <div className={styles.page_counts}>
            <p className={styles.count_title}>Rows per page: </p>
            <select
              className={styles.select}
              value={paginationData.limit}
              onChange={(e) =>
                paginationData.onLimitChange?.(Number(e.target.value))
              }
            >
              <option value="10">10</option>
              <option value="20">20</option>
              <option value="50">50</option>
              <option value="100">100</option>
            </select>
          </div>
          <div className={styles.arrows}>
            <div
              className={styles.arrow}
              onClick={() =>
                paginationData.onPageChange?.((paginationData.page || 1) - 1)
              }
            >
              <ArrowLeft />
            </div>
            <div
              className={styles.arrow}
              onClick={() =>
                paginationData.onPageChange?.((paginationData.page || 1) + 1)
              }
            >
              <ArrowRight />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
